import { z } from 'zod';

export const slugSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug inválido');

export const priceSchema = z
  .number()
  .nonnegative('O preço não pode ser negativo')
  .max(1_000_000);

export const productVariantSchema = z.object({
  id: z.string().uuid().optional(),
  size: z.string().min(1).max(20),
  colour: z.string().min(1).max(50),
  colourHex: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  stock: z.number().int().nonnegative(),
  skuSuffix: z.string().max(50).nullable().optional(),
  priceModifier: z.number().default(0),
});

// One material in a product's composition. `percentage` is an integer share
// (95 → "95% Algodão"); null means "not specified" and renders as the bare
// material name.
export const productCompositionItemSchema = z.object({
  materialId: z.string().uuid(),
  percentage: z.number().int().min(1).max(100).nullable().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
});

export const productCompositionSchema = z
  .array(productCompositionItemSchema)
  .superRefine((items, ctx) => {
    const seen = new Set<string>();
    for (const item of items) {
      if (seen.has(item.materialId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Material repetido na composição',
        });
        break;
      }
      seen.add(item.materialId);
    }
    const sum = items.reduce((acc, item) => acc + (item.percentage ?? 0), 0);
    if (sum > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A soma das percentagens não pode exceder 100%',
      });
    }
  });

// One cell of the garment measurements grid (point × size). `valueCm` null
// keeps the point selected for that product without a value yet.
export const productMeasurementCellSchema = z.object({
  pointId: z.string().uuid(),
  size: z.string().min(1).max(20),
  valueCm: z.number().min(0).max(999.9).nullable(),
});

export const productCreateSchema = z.object({
  name: z.string().min(1, 'Nome obrigatório').max(255),
  slug: slugSchema,
  description: z.string().nullable().optional(),
  shortDescription: z.string().max(500).nullable().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  price: priceSchema,
  salePrice: priceSchema.nullable().optional(),
  saleStartsAt: z.string().datetime().nullable().optional(),
  saleEndsAt: z.string().datetime().nullable().optional(),
  sku: z.string().max(100).nullable().optional(),
  weightGrams: z.number().int().nonnegative().default(0),
  metaTitle: z.string().max(255).nullable().optional(),
  metaDescription: z.string().max(500).nullable().optional(),
  isActive: z.boolean().default(true),
  highlightAsNew: z.boolean().default(true),
  variants: z.array(productVariantSchema).min(1, 'Pelo menos uma variante é obrigatória'),
  /** @deprecated Use `composition`. Kept so older clients keep working; the
   * server maps it to a composition with unspecified percentages. */
  materialIds: z.array(z.string().uuid()).optional(),
  composition: productCompositionSchema.optional(),
  measurements: z.array(productMeasurementCellSchema).optional(),
  measurementsNote: z.string().max(255).nullable().optional(),
  tagIds: z.array(z.string().uuid()).optional(),
});

export const productUpdateSchema = productCreateSchema.partial().extend({
  variants: z.array(productVariantSchema).optional(),
});

export const productListQuerySchema = z.object({
  category: z.string().optional(),
  size: z.string().optional(),
  colour: z.string().optional(),
  material: z.string().optional(),
  minPrice: z.coerce.number().optional(),
  maxPrice: z.coerce.number().optional(),
  search: z.string().optional(),
  sort: z.enum(['newest', 'price_asc', 'price_desc', 'name_asc']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(24),
});

export const productAdminListQuerySchema = z.object({
  search: z.string().optional(),
  status: z.enum(['all', 'active', 'inactive']).default('all'),
  sort: z.enum(['name', 'price', 'status', 'createdAt']).default('createdAt'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const productFacetsQuerySchema = z.object({
  category: z.string().optional(),
  size: z.string().optional(),
  colour: z.string().optional(),
  material: z.string().optional(),
  minPrice: z.coerce.number().optional(),
  maxPrice: z.coerce.number().optional(),
  search: z.string().optional(),
});

export type ProductCreateInput = z.infer<typeof productCreateSchema>;
export type ProductUpdateInput = z.infer<typeof productUpdateSchema>;
export type ProductListQuery = z.infer<typeof productListQuerySchema>;
export type ProductAdminListQuery = z.infer<typeof productAdminListQuerySchema>;
export type ProductFacetsQuery = z.infer<typeof productFacetsQuerySchema>;
export type ProductVariantInput = z.infer<typeof productVariantSchema>;
export type ProductCompositionItemInput = z.infer<typeof productCompositionItemSchema>;
export type ProductMeasurementCellInput = z.infer<typeof productMeasurementCellSchema>;

export interface ProductFacets {
  sizes: string[];
  colours: { value: string; hex: string | null }[];
  categorySlugs: string[];
  materials: { slug: string; name: string }[];
}
